"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TriangleAlert, Loader, ArrowRight, ShieldCheck, CircleAlert } from "lucide-react";
function ComplianceForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Alert className="bg-secondary/10 text-secondary border-secondary/20">
                <ShieldCheck className="size-4" />
                <AlertTitle>Inquiry Submitted Successfully</AlertTitle>
                <AlertDescription>{successMessage}</AlertDescription>
                <Button onClick={resetForm} variant="outline" className="mt-4 w-full border-secondary/30 hover:bg-secondary/20">
                    Submit Another Inquiry
                </Button>
            </Alert>;
  }
  return <Form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="company_name" rules={{
        required: "Company Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Company Name</FormLabel>
                            <FormControl>
                                <input {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" placeholder="Acme Ltd." />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="contact_name" rules={{
        required: "Contact Person is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Contact Person</FormLabel>
                            <FormControl>
                                <input {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" placeholder="John Doe" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Email Address</FormLabel>
                            <FormControl>
                                <input type="email" {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" placeholder="john.doe@example.com" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                                <input type="tel" {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" placeholder="+49 123 4567890" />
                            </FormControl>
                            <FormDescription className="text-xs">Include country code if outside the EU.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="country" rules={{
        required: "Country is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Country of Operation</FormLabel>
                            <FormControl>
                                <select {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                                    <option value="" disabled>Select your country</option>
                                    <option value="India">India</option>
                                    <option value="European Union">European Union</option>
                                    <option value="United Kingdom">United Kingdom</option>
                                    <option value="United States">United States</option>
                                    <option value="Other">Other</option>
                                </select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="inquiry_type" rules={{
        required: "Compliance Topic is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Compliance Topic</FormLabel>
                            <FormControl>
                                <select {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                                    <option value="" disabled>Select a topic</option>
                                    <option value="Import Regulations">Import Regulations</option>
                                    <option value="Export Regulations">Export Regulations</option>
                                    <option value="Customs Duties & Tariffs">Customs Duties & Tariffs</option>
                                    <option value="Product Standards & Certifications">Product Standards & Certifications</option>
                                    <option value="Data Protection & Privacy">Data Protection & Privacy</option>
                                    <option value="Other">Other</option>
                                </select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      required: "Details are required"
    }} render={({
      field
    }) => <FormItem className="mt-5">
                        <FormLabel>Details of Your Inquiry</FormLabel>
                        <FormControl>
                            <textarea {...field} className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y" placeholder="Please describe your compliance question or issue..." />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="mt-5">
                        <FormLabel>Supporting Document (optional)</FormLabel>
                        <FormControl>
                            <input type="file" onChange={e => onChange(e.target.files)} {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
                        </FormControl>
                        <FormDescription className="text-xs">Upload any relevant documents (PDF, DOCX, JPG).</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <Alert variant="destructive" className="mt-6">
                    <CircleAlert className="size-4" />
                    <AlertTitle>Submission Failed</AlertTitle>
                    <AlertDescription>{submitError}</AlertDescription>
                </Alert>}

            <Button type="submit" disabled={isSubmitting} className="w-full mt-8" size="lg">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-4" />
                        Submitting Inquiry...
                    </> : 'Submit Inquiry'}
            </Button>
        </Form>;
}
export default function ComplianceInquiry() {
  return <section id="compliance-inquiry" className="dark bg-background text-foreground py-16 md:py-24 px-4 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-accent pointer-events-none" aria-hidden="true" />

            <div className="container mx-auto max-w-7xl relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">

                    {}
                    <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/20 text-accent-foreground text-sm font-semibold border border-accent/30 shadow-sm">
                            <TriangleAlert className="size-4" />
                            High Priority Support
                        </div>

                        <div className="space-y-4">
                            <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground leading-tight">
                                Facing Compliance Issues?
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed">
                                Navigating import, export, and regional trade regulations between Europe and India can be complex. Don't let compliance hurdles slow down your business expansion.
                            </p>
                        </div>

                        <div className="space-y-5 pt-2">
                            <div className="flex items-start gap-4">
                                <div className="mt-1 bg-primary/20 p-2 rounded-lg text-primary border border-primary/20">
                                    <ShieldCheck className="size-5" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-foreground text-lg">Expert Regulatory Guidance</h4>
                                    <p className="text-sm text-muted-foreground mt-1">Get clarity on Customs Duties, Tariffs, and Product Standards to ensure seamless operations.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="mt-1 bg-primary/20 p-2 rounded-lg text-primary border border-primary/20">
                                    <ShieldCheck className="size-5" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-foreground text-lg">EU-India Trade Specialists</h4>
                                    <p className="text-sm text-muted-foreground mt-1">Leverage our deep understanding of the EU–India Free Trade Agreement to maximize your benefits.</p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-border/50">
                            <Button size="lg" className="w-full sm:w-auto gap-2 bg-accent text-accent-foreground hover:bg-accent/90 shadow-md" onClick={() => {
              const formElement = document.getElementById('compliance-form-container');
              formElement?.scrollIntoView({
                behavior: 'smooth'
              });
            }}>
                                Talk to a Consultant
                                <ArrowRight className="size-4" />
                            </Button>
                            <p className="text-xs text-muted-foreground mt-3 text-center sm:text-left">
                                Or fill out the inquiry form to get started immediately.
                            </p>
                        </div>
                    </div>

                    {}
                    <div className="lg:col-span-7" id="compliance-form-container">
                        <div className="bg-card border border-border rounded-2xl p-6 md:p-10 shadow-2xl shadow-black/40 relative overflow-hidden">
                            {}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary" />
                            
                            <div className="mb-8">
                                <h3 className="text-2xl md:text-3xl font-bold text-card-foreground tracking-tight">Compliance Inquiry Form</h3>
                                <p className="text-muted-foreground mt-2 text-sm md:text-base">
                                    Provide details about your regulatory challenges, and our experts will get back to you with actionable insights.
                                </p>
                            </div>

                            <FormProvider formId="compliance_inquiry_form_regulatory" sectionName="Compliance Inquiry" defaultValues={{
              company_name: "",
              contact_name: "",
              email: "",
              phone: "",
              country: "",
              inquiry_type: "",
              message: "",
              attachment: ""
            }} validationRules={{
              company_name: {
                required: true
              },
              contact_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              country: {
                required: true
              },
              inquiry_type: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Submit Inquiry" successMessage="Thank you for your inquiry. Our compliance experts will review your details and contact you shortly.">
                                <ComplianceForm />
                            </FormProvider>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}